import { type ClassValue, clsx } from 'clsx'
import {
  camelCase,
  isArray,
  isEmpty,
  isNil,
  isObject,
  isString,
  keyBy,
  map,
  snakeCase,
  toNumber,
  transform,
} from 'lodash'
import { twMerge } from 'tailwind-merge'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { SetupIntent } from '@stripe/stripe-js'

import { COUNTRY_CODES } from '@/constants/countryCodes'
import { NonNullableProps } from '@/lib/types/utilsT'
import { Company, CompanyPermissionTypes } from '@/lib/types/CompanyT'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const camelize = (obj: any): any => {
  if (isArray(obj)) {
    return obj.map(camelize)
  } else if (isObject(obj)) {
    return transform(obj, (acc: Record<string, any>, value, key, target) => {
      const camelKey = isArray(target) ? key : camelCase(key as string)
      acc[camelKey] = isObject(value) || isArray(value) ? camelize(value) : value
    })
  }
  return obj
}

export const snakeize = <T extends Record<string, any>>(obj: T): T => {
  return transform(obj, (acc: Record<string, any>, value, key) => {
    const snakeKey = snakeCase(key)
    acc[snakeKey] = isObject(value) && !(value instanceof File) ? snakeize(value) : value
  }) as T
}

export const getTokenRemainingMs = (token: string | null): number => {
  if (!token) return 0

  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (!payload.exp) return 0

    const now = Date.now() / 1000
    const remainingSeconds = payload.exp - now

    return Math.max(0, remainingSeconds * 1000)
  } catch {
    return 0
  }
}

export const mergeByKey = <T extends Record<string, any>>(
  baseArray: T[],
  overrideArray: T[],
  key: keyof T
): T[] => {
  const overrideByKey = keyBy(overrideArray, key as string)
  return map(baseArray, (item: any) => ({
    ...item,
    ...overrideByKey[item[key] as string], // merge if exists
  }))
}

export const getTopLevelTrueKeys = (obj: Record<string, any>): string[] => {
  return Object.entries(obj)
    .filter(([_, value]) => {
      if (value === true) return true

      if (Array.isArray(value)) {
        return value.some((item) =>
          typeof item === 'object' && item !== null
            ? Object.values(item).some((v) => v === true)
            : item === true
        )
      }

      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some((v) => v === true)
      }

      return false
    })
    .map(([key]) => key)
}

export const getWordInitials = (word: string | undefined): string => {
  if (!word) return ''

  const words = word.trim().split(/\s+/) // split by whitespace
  if (words.length < 2) {
    return words[0]?.[0]?.toUpperCase() ?? ''
  }

  return `${words[0][0]}${words[1][0]}`.toUpperCase()
}
export const isNullOrEmpty = <T>(value: T | undefined | null): value is undefined | null =>
  isNil(value) || (isEmpty(value) && (isArray(value) || isString(value)))

export const countFormErrors = (errorNode: unknown): number => {
  if (!errorNode || typeof errorNode !== 'object') return 0
  if ('message' in errorNode && typeof (errorNode as { message?: unknown }).message === 'string') {
    return 1
  }

  return Object.values(errorNode as Record<string, unknown>).reduce(
    (sum: number, child) => sum + countFormErrors(child),
    0
  )
}

export const formatDate = (date?: string | number | Date | null): string => {
  if (!date) return ''
  const d = new Date(date)
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function formatDateWithTime(date?: string | number | Date): string {
  if (!date) return ''

  const d = new Date(date)
  const now = new Date()

  // Reset time to midnight for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  const dateToCheck = new Date(d.getFullYear(), d.getMonth(), d.getDate())

  // Format time
  const timeString = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  // Check if it's today or yesterday
  if (dateToCheck.getTime() === today.getTime()) {
    return `Today at ${timeString}`
  } else if (dateToCheck.getTime() === yesterday.getTime()) {
    return `Yesterday at ${timeString}`
  }

  // Otherwise show full date with time
  const dateString = d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

  return `${dateString} at ${timeString}`
}

export const formatTimezoneLabel = (tz?: string | null): string => {
  try {
    if (!tz) return 'N/A'

    const now = new Date()

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(now)

    const offsetPart = parts.find(
      (part): part is Intl.DateTimeFormatPart & { type: 'timeZoneName' } =>
        part.type === 'timeZoneName'
    )

    const rawOffset = offsetPart?.value ?? 'GMT'

    // "GMT+2" → "+2", "GMT+02:00" → "+2"
    const offset = rawOffset.replace('GMT', '').replace(':00', '') || '+0'

    const city = tz.split('/').pop()?.replace(/_/g, ' ')

    if (!city) return 'N/A'

    return `UTC${offset} • ${city}`
  } catch {
    return 'N/A'
  }
}

export const getAmericaEuropeTimezones = () => {
  return Intl.supportedValuesOf('timeZone')
    .filter((tz) => tz.startsWith('America/') || tz.startsWith('Europe/'))
    .map((tz) => ({
      value: tz,
      label: formatTimezoneLabel(tz),
    }))
    .filter((item) => item.label !== null)
}

export const formatPhoneNumber = (input: string | number | null | undefined): string => {
  if (!input) return ''
  const str = input.toString().replace(/\s+/g, '')

  if (!str.startsWith('+')) return str

  // Find the longest matching country dial code
  const country = COUNTRY_CODES.filter((c: any) => str.startsWith(c.dialCode)).sort(
    (a: any, b: any) => b.dialCode.length - a.dialCode.length
  )[0]

  if (!country) return groupDefault(str)

  const dialCode = country.dialCode
  const numberPart = str.slice(dialCode.length)

  // Try regex-based formatting
  if (country.regex && country.format) {
    const match = str.match(new RegExp(country.regex))
    if (match) return country.format.replace(/\$(\d+)/g, (_, i) => match[i] || '')
  }

  // Try mask-based formatting
  if (country.mask) {
    let formatted = ''
    let i = 0
    for (const ch of country.mask) {
      formatted += ch === '0' ? (numberPart[i++] ?? '') : ch
      if (i >= numberPart.length) break
    }
    if (i < numberPart.length) formatted += numberPart.slice(i)
    return `${dialCode} ${formatted.trim()}`
  }

  // Fallback grouping
  return `${dialCode} ${groupDefault(numberPart)}`
}

// Simple fallback grouping
const groupDefault = (str: string): string =>
  str
    .replace(/^\+/, '')
    .match(/.{1,3}/g)
    ?.join(' ') ?? str

export const generateJobKey = (args: Record<string, any>) => {
  if (!isObject(args)) {
    throw new Error('Job key args must be an object')
  }

  return Object.entries(args)
    .sort(([a], [b]) => a.localeCompare(b))
    .reduce((acc, [key, value]) => `${acc}_${snakeCase(key)}_${value}`, '')
}

export const isNumeric = (value: any) => !isNaN(toNumber(value)) && isFinite(toNumber(value))

export const extractPaymentMethodId = (setupIntent: SetupIntent) => {
  if (isString(setupIntent.payment_method)) {
    return setupIntent.payment_method
  }

  if (isObject(setupIntent.payment_method)) {
    return setupIntent.payment_method.id
  }

  if (isNil(setupIntent.payment_method)) {
    return null
  }

  return null
}
export const formatPrice = (price?: number | string) => {
  const num = typeof price === 'string' ? Number(price) : price

  if (typeof num !== 'number' || isNaN(num)) {
    return ''
  }

  return Number.isInteger(num) ? num.toString() : num.toFixed(2)
}

export const normalizeUrl = (value: string) => {
  if (!value) return value

  if (!/^https?:\/\//i.test(value)) {
    return `https://${value}`
  }

  return value
}

export const hasCompanyPermissions = (
  companyPermissions: Company['permissions'],
  permission: CompanyPermissionTypes
) => !!companyPermissions?.some((p) => p.name === permission)

export const omitEmpty = <T extends Record<string, any>>(obj: T): Partial<NonNullableProps<T>> => {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => !isNullOrEmpty(v))) as Partial<
    NonNullableProps<T>
  >
}

export const hasPhoneNumberDigits = (phone: string): boolean => {
  const matchedCountry = COUNTRY_CODES.find((country) => phone.startsWith(country.dialCode))
  if (!matchedCountry) return false

  const phoneWithoutCode = phone.slice(matchedCountry.dialCode.length).replace(/\D/g, '')

  return phoneWithoutCode.length > 0
}

export const validatePhoneNumber = (
  phone: string,
  options?: {
    allowEmpty?: boolean
  }
): boolean => {
  const value = phone?.trim()

  if (!value) {
    return options?.allowEmpty ?? false
  }

  const normalized = value.startsWith('+') ? value : `+${value.replace(/\D/g, '')}`

  const matchedCountry = [...COUNTRY_CODES]
    .sort((a, b) => b.dialCode.length - a.dialCode.length)
    .find((country) => normalized.startsWith(country.dialCode))

  if (!matchedCountry) {
    return false
  }

  try {
    const phoneNumber = parsePhoneNumberFromString(normalized)

    if (!phoneNumber?.isValid()) {
      return false
    }

    return phoneNumber.country === matchedCountry.code
  } catch {
    return false
  }
}

export const appendFormData = (formData: FormData, data: any, parentKey?: string) => {
  if (data === undefined) return

  if (data === null) {
    formData.append(parentKey!, 'null')
    return
  }

  if (data instanceof File) {
    formData.append(parentKey!, data)
    return
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      formData.append(parentKey!, '[]')
      return
    }

    data.forEach((item, index) => {
      appendFormData(formData, item, `${parentKey}[${index}]`)
    })

    return
  }

  if (typeof data === 'object') {
    Object.entries(data).forEach(([key, value]) => {
      appendFormData(
        formData,
        value,
        parentKey ? `${parentKey}[${snakeCase(key)}]` : snakeCase(key)
      )
    })

    return
  }

  formData.append(parentKey!, String(data))
}
